import React, { useEffect, useRef, useState } from 'react';
import { renderCell, renderCellDate, isEmptyCell } from '../utils/helpers';

const EditableCell = ({
  cellKey,
  editingKey,
  onStartEdit,
  onEndEdit,
  value,
  onSave,
  type = 'text',
  className = '',
  disabled = false,
  saving = false,
  placeholder = 'Double-clic pour saisir',
}) => {
  const isEditing = editingKey === cellKey;
  const [draft, setDraft] = useState(value ?? '');
  const inputRef = useRef(null);
  const committedRef = useRef(false);

  useEffect(() => {
    setDraft(value ?? '');
  }, [value]);

  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      if (type === 'text' && inputRef.current.select) {
        inputRef.current.select();
      }
    }
    if (!isEditing) {
      committedRef.current = false;
    }
  }, [isEditing, type]);

  const handleSave = async () => {
    if (committedRef.current) return;
    committedRef.current = true;

    const nextValue = type === 'date' ? draft : draft.trim();
    const currentValue = type === 'date' ? (value ?? '') : (value ?? '').trim();

    onEndEdit();
    if (nextValue !== currentValue) {
      await onSave(nextValue);
    }
  };

  const handleCancel = () => {
    committedRef.current = true;
    setDraft(value ?? '');
    onEndEdit();
  };

  if (isEditing) {
    return (
      <td className={className}>
        <input
          ref={inputRef}
          type={type}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          placeholder={type === 'date' ? '' : placeholder}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              handleSave();
            } else if (e.key === 'Escape') {
              e.preventDefault();
              handleCancel();
            }
          }}
          onBlur={() => {
            if (committedRef.current) {
              committedRef.current = false;
              return;
            }
            setTimeout(handleSave, 100);
          }}
          className="w-full min-w-[80px] px-1.5 py-1 border-2 border-red-400 rounded text-sm focus:outline-none focus:ring-1 focus:ring-red-300"
          disabled={saving}
        />
      </td>
    );
  }

  const display = type === 'date' ? renderCellDate(value) : renderCell(value);
  const isEmpty = isEmptyCell(value);

  return (
    <td
      className={`${className} ${disabled ? '' : 'cursor-text cell-editable hover:bg-red-50 hover:outline hover:outline-dashed hover:outline-red-200/60'}`}
      title={disabled ? undefined : 'Double-clic pour modifier'}
      onDoubleClick={() => {
        if (disabled || saving) return;
        onStartEdit(cellKey);
      }}
    >
      {isEmpty && type !== 'date' ? (
        <span className="text-gray-400 italic">{placeholder}</span>
      ) : (
        display
      )}
    </td>
  );
};

export default EditableCell;
